import React, { useState } from 'react';
import { Alert, Button, Content, Form, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminGpuActions } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuServer } from '../../../types/models/Verda';
import { NoteField, isValidNote } from './VerdaGpuParts';

interface VerdaResolveServerModalProps {
  server: VerdaGpuServer;
  onClose: () => void;
  /** Something may have changed: the detail refetches. */
  onChanged: () => void;
}

/**
 * `POST /admin/verda/servers/{id}/resolve` — records the staff decision on a finished (`lost`,
 * `failed`, `destroyed`) server, once. For a `lost` server it has consequences, spelled out here.
 * Mounted only while open.
 */
function VerdaResolveServerModal({ server, onClose, onChanged }: VerdaResolveServerModalProps) {
  const { resolve, busy } = useAdminGpuActions();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  const handleResolve = async () => {
    setError(null);
    setNoteError(null);
    const outcome = await resolve(server.id, note.trim());
    if (outcome.status === 'error') {
      setError(outcome.message);
      setNoteError(outcome.fieldErrors.note ?? null);
      addNotification({
        title: `Could not resolve ${server.hostname}`,
        srTitle: 'Error alert',
        description: outcome.message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return;
    }
    addNotification({
      title: `${server.hostname} resolved`,
      srTitle: 'Success alert',
      description: 'The decision is recorded on the server.',
      variant: 'success',
    });
    setDrawerOpen(true);
    onChanged();
    onClose();
  };

  return (
    <Modal variant="small" isOpen onClose={busy ? undefined : onClose} aria-labelledby="verda-resolve-title">
      <ModalHeader title="Resolve server" labelId="verda-resolve-title" description={server.hostname} />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not resolve the server" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {server.status === 'lost' ? (
          <Alert variant="warning" isInline title="Resolving a lost server has consequences" className="pf-v6-u-mb-md">
            <Content component="p">
              Resolving starts the monthly invoicing of its remaining unrefunded hours — after which they can no longer
              be refunded here — and a still-listed Verda instance becomes a leftover the meter deletes while the
              destroy switch is on.
            </Content>
            <Content component="p">Suggested order: refund → destroy → resolve.</Content>
          </Alert>
        ) : (
          <Content component="p">
            This records your decision on <strong>{server.hostname}</strong>. A server can be resolved only once.
          </Content>
        )}
        <Form onSubmit={(event) => event.preventDefault()}>
          <NoteField
            id="verda-resolve-note"
            label="Note"
            value={note}
            onChange={setNote}
            error={noteError}
            hint="What was decided, e.g. “Refunded the current hour; customer told”."
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleResolve()}
          isLoading={busy}
          isDisabled={busy || !isValidNote(note)}
        >
          Resolve server
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerdaResolveServerModal;
